import useGetDashboardInvoiceStats from '@/api/queries/dashboard/useGetDashboardInvoiceStats';
import useGetDashboardProposalStats from '@/api/queries/dashboard/useGetDashboardProposalStats';
import { currencyLabel } from '@/helpers/currency';
import { useTranslation } from '@/i18n';
import { useAccount } from '@/utils/storeHooks';
import { Card, DonutChart, DonutSlice, Tooltip } from '@payaca/ambrosia';

import { currencyCodeForRegion } from '@payaca/helpers/currencyHelper';
import { AccountRegions } from '@payaca/types/accountTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useCallback, useMemo } from 'react';
import { DateRange } from '../utils';
import DashboardSectionError from './DashboardSectionError';
import DashboardSectionSkeleton from './DashboardSectionSkeleton';

interface DashboardStatusBreakdownProps {
  dateRange?: DateRange;
}

interface LegendItemProps {
  color: string;
  label: string;
  value: string;
  count: number;
}

const LegendItem: FC<LegendItemProps> = ({ color, label, value, count }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-2">
      <div
        className="size-3 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <p>{label}</p>
    </div>
    <div className="text-right">
      <p>{value}</p>
      <p className="supporting-body text-gray-500">{count}</p>
    </div>
  </div>
);

interface BreakdownCardProps {
  title: string;
  tooltip?: string;
  slices: DonutSlice[];
  legendItems: LegendItemProps[];
  emptyMessage: string;
  tooltipFormatter?: (value: number) => string;
}

const BreakdownCard: FC<BreakdownCardProps> = ({
  title,
  tooltip,
  slices,
  legendItems,
  emptyMessage,
  tooltipFormatter,
}) => {
  const hasData = slices.some((s) => s.value > 0);

  return (
    <Card className="h-full">
      <Card.Body>
        <h3 className="mb-4 flex items-center gap-1">
          {title}
          {tooltip && (
            <Tooltip tooltipContent={tooltip} className="translate-y-0.5">
              <UntitledIcon
                name="info-circle.3"
                className="size-3.5 text-gray-400"
              />
            </Tooltip>
          )}
        </h3>
        {hasData ? (
          <div className="flex flex-col items-center gap-4 @screen-sm:flex-row @screen-sm:items-start">
            <div className="shrink-0">
              <DonutChart
                slices={slices}
                height={160}
                width={160}
                tooltipFormatter={tooltipFormatter}
              />
            </div>
            <div className="w-full flex-1 divide-y divide-gray-100">
              {legendItems.map((item) => (
                <LegendItem key={item.label} {...item} />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex h-40 items-center justify-center text-center">
            <p className="supporting-body text-gray-500">{emptyMessage}</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const DashboardStatusBreakdown: FC<DashboardStatusBreakdownProps> = ({
  dateRange,
}) => {
  const account = useAccount();
  const translate = useTranslation('dashboard.breakdown');
  const translateErrors = useTranslation('dashboard.errors');

  const {
    stats: proposalStats,
    isLoading: proposalLoading,
    isError: proposalError,
  } = useGetDashboardProposalStats({ dateRange });
  const {
    stats: invoiceStats,
    isLoading: invoiceLoading,
    isError: invoiceError,
  } = useGetDashboardInvoiceStats({ dateRange });

  const currencyCode = currencyCodeForRegion[account?.region as AccountRegions];
  const formatCurrency = useCallback(
    (value: number) => currencyLabel(value, currencyCode),
    [currencyCode]
  );

  const proposalData = useMemo(() => {
    if (!proposalStats) return { slices: [], legendItems: [], totalValue: 1 };

    const pending = Math.max(
      0,
      proposalStats.sent.count -
        proposalStats.accepted.count -
        proposalStats.declined.count
    );
    const pendingValue = Math.max(
      0,
      proposalStats.sent.totalValue.value -
        proposalStats.accepted.totalValue.value -
        proposalStats.declined.totalValue.value
    );
    const totalValue = proposalStats.sent.totalValue.value || 1;

    const slices: DonutSlice[] = [
      {
        label: translate('pending'),
        value: pendingValue,
        colour: '#a855f7', // purple-500
      },
      {
        label: translate('accepted'),
        value: proposalStats.accepted.totalValue.value,
        colour: '#22c55e', // green-500
      },
      {
        label: translate('declined'),
        value: proposalStats.declined.totalValue.value,
        colour: '#ef4444', // red-500
      },
    ];

    const legendItems: LegendItemProps[] = [
      {
        color: '#a855f7', // purple-500
        label: translate('pending'),
        value: formatCurrency(pendingValue),
        count: pending,
      },
      {
        color: '#22c55e', // green-500
        label: translate('accepted'),
        value: formatCurrency(proposalStats.accepted.totalValue.value),
        count: proposalStats.accepted.count,
      },
      {
        color: '#ef4444', // red-500
        label: translate('declined'),
        value: formatCurrency(proposalStats.declined.totalValue.value),
        count: proposalStats.declined.count,
      },
    ];

    return { slices, legendItems, totalValue };
  }, [proposalStats, formatCurrency, translate]);

  const invoiceData = useMemo(() => {
    if (!invoiceStats) return { slices: [], legendItems: [], totalValue: 1 };

    const awaiting = Math.max(
      0,
      invoiceStats.sent.count - invoiceStats.paid.count - invoiceStats.due.count
    );
    const awaitingValue = Math.max(
      0,
      invoiceStats.sent.totalValue.value -
        invoiceStats.paid.totalValue.value -
        invoiceStats.due.totalValue.value
    );
    const totalValue = invoiceStats.sent.totalValue.value || 1;

    const slices: DonutSlice[] = [
      {
        label: translate('paid'),
        value: invoiceStats.paid.totalValue.value,
        colour: '#22c55e', // green-500
      },
      {
        label: translate('awaiting'),
        value: awaitingValue,
        colour: '#3b82f6', // blue-500
      },
      {
        label: translate('overdue'),
        value: invoiceStats.due.totalValue.value,
        colour: '#eab308', // yellow-500
      },
    ];

    const legendItems: LegendItemProps[] = [
      {
        color: '#22c55e', // green-500
        label: translate('paid'),
        value: formatCurrency(invoiceStats.paid.totalValue.value),
        count: invoiceStats.paid.count,
      },
      {
        color: '#3b82f6', // blue-500
        label: translate('awaiting'),
        value: formatCurrency(awaitingValue),
        count: awaiting,
      },
      {
        color: '#eab308', // yellow-500
        label: translate('overdue'),
        value: formatCurrency(invoiceStats.due.totalValue.value),
        count: invoiceStats.due.count,
      },
    ];

    return { slices, legendItems, totalValue };
  }, [invoiceStats, formatCurrency, translate]);

  if (proposalLoading || invoiceLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 @screen-lg:grid-cols-2">
        <DashboardSectionSkeleton variant="donut" />
        <DashboardSectionSkeleton variant="donut" />
      </div>
    );
  }

  if (proposalError || invoiceError) {
    return (
      <DashboardSectionError message={translateErrors('breakdownError')} />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 @screen-lg:grid-cols-2">
      <BreakdownCard
        title={translate('proposalStatus')}
        tooltip={translate('proposalStatusTooltip')}
        slices={proposalData.slices}
        legendItems={proposalData.legendItems}
        emptyMessage={translate('noProposals')}
        tooltipFormatter={(val) =>
          `${formatCurrency(val)} (${(
            (val / proposalData.totalValue) *
            100
          ).toFixed(0)}%)`
        }
      />
      <BreakdownCard
        title={translate('invoiceStatus')}
        slices={invoiceData.slices}
        legendItems={invoiceData.legendItems}
        emptyMessage={translate('noInvoices')}
        tooltipFormatter={(val) =>
          `${formatCurrency(val)} (${(
            (val / invoiceData.totalValue) *
            100
          ).toFixed(0)}%)`
        }
      />
    </div>
  );
};

export default DashboardStatusBreakdown;
